import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Send, X, Headset, Loader2, PackageOpen, ReceiptText, MessageCircle, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const QUICK = ["When is my next delivery?", "Track my last order", "How do I return stock?"];

function RobAvatar({ size = 32, name = "Rob" }) {
  return <div className="flex shrink-0 items-center justify-center rounded-full bg-[var(--brand)] font-extrabold text-white" style={{ width: size, height: size, fontSize: size * 0.42 }}>{name[0]}</div>;
}

function Card({ c, onProduct, onOrder, brand }) {
  if (c.type === "guide") {
    return (
      <a data-testid="rob-guide-card" href={brand.guide} target="_blank" rel="noreferrer" className="flex w-full items-center gap-2 rounded-lg border border-[#e5e9f0] bg-white p-2 text-left text-xs hover:border-[var(--brand)]">
        <FileText size={16} className="text-[var(--brand)]" />
        <div className="flex-1"><div className="font-semibold">{brand.guideTitle}</div><div className="text-[#6a6d70]">Every screen, step by step (PDF)</div></div>
      </a>
    );
  }
  if (c.type === "order") {
    return (
      <button data-testid={`rob-order-${c.order_no}`} onClick={() => onOrder(c.order_no)} className="flex w-full items-center gap-2 rounded-lg border border-[#e5e9f0] bg-white p-2 text-left text-xs hover:border-[var(--brand)]">
        <ReceiptText size={16} className="text-[var(--brand)]" />
        <div className="flex-1"><div className="font-mono font-semibold">{c.order_no}</div><div className="text-[#6a6d70]">{c.status} · {c.lines} lines · {gbp(c.value)}</div></div>
      </button>
    );
  }
  return (
    <button data-testid={`rob-product-${c.id}`} onClick={() => onProduct(c.id)} className="flex w-full items-center gap-2 rounded-lg border border-[#e5e9f0] bg-white p-1.5 text-left text-xs hover:border-[var(--brand)]">
      {c.has_image ? <img src={`${API}/api/shop/media/${c.id}?w=120`} alt="" className="h-10 w-10 rounded object-contain" /> : <PackageOpen size={18} className="m-2 text-[#9aa0a6]" />}
      <div className="flex-1"><div className="line-clamp-1 font-medium">{c.title}</div><div className="text-[#6a6d70]">{c.price > 0 ? gbp(c.price) : "POA"} · {c.sap_code}</div></div>
    </button>
  );
}

function Bubble({ m, onProduct, onOrder, brand }) {
  if (m.role === "system") return <div className="my-1 text-center text-[11px] text-[#6a6d70]">{m.text}</div>;
  const mine = m.role === "user";
  return (
    <div className={`flex gap-2 ${mine ? "justify-end" : ""}`}>
      {!mine && (m.role === "agent" ? <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#161d23] text-white"><Headset size={15} /></div> : <RobAvatar name={brand.botName} />)}
      <div className="max-w-[80%] space-y-1.5">
        {m.role === "agent" && <div className="text-[10px] font-semibold text-[#6a6d70]">{m.name} · EP Group</div>}
        <div className={`whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${mine ? "rounded-br-sm bg-[var(--brand)] text-white" : "rounded-bl-sm bg-white text-[#32363a] shadow-sm"}`}>{m.text || <Loader2 size={14} className="animate-spin" />}</div>
        {(m.cards || []).map((c, i) => <Card key={i} c={c} onProduct={onProduct} onOrder={onOrder} brand={brand} />)}
      </div>
    </div>
  );
}

function useRobChat(open) {
  const [chat, setChat] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/shop/support/chat").then((r) => setChat(r.data.chat)).catch(() => {}), []);
  useEffect(() => { if (open && !chat) { load(); } }, [open, chat, load]);
  const live = chat && (chat.status === "waiting" || chat.status === "live");
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [live, load]);

  const send = async (text) => {
    if (!text.trim() || busy || !chat) return;
    setBusy(true);
    const user = { id: `u${Date.now()}`, role: "user", text };
    if (chat.status !== "bot") {
      setChat((c) => ({ ...c, messages: [...c.messages, user] }));
      await api.post("/shop/support/chat/message", { text }).catch(() => {});
      setBusy(false); load(); return;
    }
    const rid = `r${Date.now()}`;
    setChat((c) => ({ ...c, messages: [...c.messages, user, { id: rid, role: "rob", text: "" }] }));
    const patch = (fn) => setChat((c) => ({ ...c, messages: c.messages.map((m) => (m.id === rid ? fn(m) : m)) }));
    try {
      const res = await fetch(`${API}/api/shop/support/chat/message`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) patch((m) => ({ ...m, text: m.text + ev.delta }));
          if (ev.done) patch((m) => ({ ...m, cards: ev.cards }));
        }
      }
    } catch { patch((m) => ({ ...m, text: "Sorry, something went wrong. Try again or talk to a person." })); }
    setBusy(false);
  };
  const handoff = async () => { const r = await api.post("/shop/support/chat/handoff"); setChat(r.data.chat); };
  const endChat = async () => { await api.post("/shop/support/chat/close"); setChat(null); load(); };
  const newChat = async () => { const r = await api.post("/shop/support/chat/ack"); setChat(r.data.chat); };
  return { chat, busy, send, handoff, endChat, newChat };
}

export function AskRob({ open, setOpen, humanReq, setDetail, brand }) {
  const bot = brand.botName || "Rob";
  const quick = brand.quick || QUICK;
  const { chat, busy, send, handoff, endChat, newChat } = useRobChat(open);
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat?.messages?.length, chat?.messages?.[chat?.messages?.length - 1]?.text]);
  useEffect(() => { if (humanReq && chat && chat.status === "bot") { handoff(); } }, [humanReq, chat?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (t) => { send(t); setInput(""); };
  const status = chat?.status;
  const statusLabel = status === "waiting" ? "Waiting for an agent…" : status === "live" ? `Chatting with ${chat.agent_name}` : status === "closed" ? "Chat closed" : "AI assistant · usually instant";

  if (!open) {
    return (
      <button data-testid="ask-rob-launcher" onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-[var(--brand)] py-2 pl-2 pr-4 text-sm font-semibold text-white shadow-xl transition hover:-translate-y-0.5 hover:bg-[var(--brand-hover)]">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20"><MessageCircle size={17} /></span> Ask {bot}
      </button>
    );
  }
  return (
    <div data-testid="ask-rob-panel" className="fixed inset-0 z-50 flex flex-col bg-[#f5f6f7] shadow-2xl sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[600px] sm:w-[390px] sm:rounded-2xl sm:border sm:border-[#e5e9f0]">
      <div className="flex items-center gap-3 bg-[#161d23] px-4 py-3 text-white sm:rounded-t-2xl">
        <RobAvatar size={36} name={bot} />
        <div className="flex-1"><div className="text-sm font-bold">Ask {bot}</div><div data-testid="rob-status" className="text-[11px] text-white/60">{statusLabel}</div></div>
        <button data-testid="ask-rob-close" onClick={() => setOpen(false)} className="rounded-md p-1.5 hover:bg-white/10"><X size={18} /></button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="rob-messages">
        {!chat && <Loader2 className="mx-auto animate-spin text-[#9aa0a6]" />}
        {chat?.messages?.map((m) => <Bubble key={m.id} m={m} brand={brand} onProduct={setDetail} onOrder={(no) => setDetail({ order_no: no })} />)}
        {status === "bot" && chat.messages.length <= 1 && (
          <div className="flex flex-wrap gap-1.5">{quick.map((q, i) => <button key={q} data-testid={`rob-quick-${i}`} onClick={() => go(q)} className="rounded-full border border-[var(--brand)]/40 bg-white px-3 py-1 text-xs text-[#32363a] hover:border-[var(--brand)]">{q}</button>)}</div>
        )}
        <div ref={endRef} />
      </div>
      <div className="flex items-center justify-between border-t border-[#e5e9f0] bg-white px-3 py-1.5 text-xs">
        {status === "bot"
          ? <button data-testid="rob-handoff" onClick={handoff} className="inline-flex items-center gap-1 font-semibold text-[var(--brand)] hover:underline"><Headset size={13} /> Talk to a person</button>
          : <span className="text-[#6a6d70]">{chat?.chat_no}</span>}
        <a data-testid="rob-user-guide" href={brand.guide} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#6a6d70] hover:text-[var(--brand)]"><FileText size={13} /> User guide</a>
        {chat && status !== "closed" && chat.messages.length > 1 && <button data-testid="rob-end" onClick={endChat} className="text-[#6a6d70] hover:text-[#bb0000]">End chat</button>}
      </div>
      {status === "closed" ? (
        <div className="bg-white p-3 sm:rounded-b-2xl"><button data-testid="rob-new-chat" onClick={newChat} className="w-full rounded-full bg-[var(--brand)] py-2 text-sm font-semibold text-white hover:bg-[var(--brand-hover)]">Start a new chat</button></div>
      ) : (
      <form onSubmit={(e) => { e.preventDefault(); go(input); }} className="flex gap-2 bg-white p-3 sm:rounded-b-2xl">
        <input data-testid="rob-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={status === "bot" ? `Ask ${bot} anything…` : "Message the support team…"} className="flex-1 rounded-full border border-[#c8cfda] px-4 py-2 text-sm outline-none focus:border-[var(--brand)]" />
        <button data-testid="rob-send" disabled={busy || !input.trim() || !chat} className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand)] text-white disabled:opacity-40"><Send size={15} /></button>
      </form>
      )}
    </div>
  );
}
